import React from 'react';
import { DropdownWrapper, List, Item } from './SearchSelect';
import { FormattedMessage } from 'react-intl';
import { IErrorMessage } from './errorMessage/errorMessage.type';
import InputField from './InputField';
import ValueFormatters from '../fields/valueFormatters';
import { TEXT_TO_LOWERCASE } from '../constants/styles';

interface ISearchInputProps<T, K> {
    id: string;
    selectedItem?: T;
    generateSuggestions: (value: string) => T[];
    searchKey: K;
    label: FormattedMessage.MessageDescriptor | string;
    placeholder?: string;
    required?: boolean;
    error?: IErrorMessage;
    qaId?: string;
    value: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onItemSelected?: (item: T) => void;
    toLowercase?: boolean;
}

interface ISearchInputState<T> {
    selectedItem: T | undefined;
    suggestions: T[];
    innerValue: string | null;
    isOpen: boolean;
    highlightedIndex: number;
}

export default class SuggestionsInput<T, K extends keyof T> extends React.Component<
    ISearchInputProps<T, K>,
    ISearchInputState<T>
> {
    wrapperRef: React.RefObject<HTMLDivElement>;

    constructor(props: ISearchInputProps<T, K>) {
        super(props);

        const { selectedItem, value } = this.props;

        this.state = {
            selectedItem,
            suggestions: [],
            innerValue: value,
            isOpen: false,
            highlightedIndex: 0,
        };

        this.generateSuggestionsHandle = this.generateSuggestionsHandle.bind(this);
        this.getSearchValue = this.getSearchValue.bind(this);

        this.openSuggestions = this.openSuggestions.bind(this);
        this.closeSuggestions = this.closeSuggestions.bind(this);

        this.InputKeyEventHandle = this.InputKeyEventHandle.bind(this);
        this.onItemSelect = this.onItemSelect.bind(this);

        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.setHighlightedIndex = this.setHighlightedIndex.bind(this);

        this.wrapperRef = React.createRef<HTMLDivElement>();
    }

    private generateSuggestionsHandle(event: React.ChangeEvent<HTMLInputElement>) {
        const { generateSuggestions } = this.props;
        const { isOpen } = this.state;

        const inputValue = event.target.value;
        const suggestions = generateSuggestions(inputValue);
        this.setState({
            suggestions: suggestions,
            innerValue: inputValue,
        });

        if (!isOpen) {
            this.openSuggestions();
        }
    }

    private getSearchValue(item: T): string {
        const { searchKey } = this.props;
        const key = item && item[searchKey] ? String(item[searchKey]) : '';
        return key;
    }

    private openSuggestions() {
        this.setState({ isOpen: true });
    }

    private closeSuggestions() {
        this.setState({ isOpen: false });
    }

    private onItemSelect(item: T) {
        const { onItemSelected } = this.props;
        const selectedItemValue = this.getSearchValue(item);
        this.setState({
            innerValue: selectedItemValue,
        });
        if (onItemSelected) {
            onItemSelected(item);
        }
        this.closeSuggestions();
    }

    private setHighlightedIndex(index: number) {
        const { suggestions } = this.state;
        const maxLength = suggestions.length;

        if (index >= maxLength) {
            index = 0;
        }

        if (index < 0) {
            index = maxLength - 1;
        }

        this.setState({
            highlightedIndex: index,
        });
    }

    private InputKeyEventHandle(event: React.KeyboardEvent<HTMLInputElement>) {
        const { highlightedIndex, isOpen } = this.state;
        if (!isOpen) {
            return;
        }

        if (event.key === 'ArrowDown') {
            const nextHighlightedIndex = highlightedIndex + 1;
            this.setHighlightedIndex(nextHighlightedIndex);
            event.preventDefault();
            return;
        }

        if (event.key === 'ArrowUp') {
            const nextHighlightedIndex = highlightedIndex - 1;
            this.setHighlightedIndex(nextHighlightedIndex);
            event.preventDefault();
            return;
        }

        if (event.key === 'Enter') {
            const { highlightedIndex, suggestions } = this.state;
            const item = suggestions[highlightedIndex];
            const value = this.getSearchValue(item);
            this.setState({
                innerValue: value,
            });
            this.closeSuggestions();
            return;
        }

        if (event.key === 'Tab') {
            this.closeSuggestions();
            return;
        }
    }

    private handleClickOutside(event: any) {
        if (this.wrapperRef.current && !this.wrapperRef.current.contains(event.target)) {
            this.closeSuggestions();
        }
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    render() {
        const {
            id,
            label,
            required,
            placeholder,
            error,
            onBlur,
            qaId,
            toLowercase = false,
        } = this.props;

        const { suggestions, innerValue, isOpen, highlightedIndex } = this.state;

        return (
            <div ref={this.wrapperRef}>
                <DropdownWrapper onBlur={this.handleClickOutside}>
                    <InputField
                        autoComplete="email"
                        id={id}
                        name={id}
                        labelMessage={label}
                        required={required}
                        placeholder={placeholder}
                        error={error}
                        value={innerValue ?? ''}
                        onChange={this.generateSuggestionsHandle}
                        onFocus={() => {
                            this.openSuggestions();
                        }}
                        style={toLowercase ? TEXT_TO_LOWERCASE : {}}
                        onKeyDown={this.InputKeyEventHandle}
                        onBlur={onBlur}
                    />
                    {isOpen && (
                        <List
                            data-qa-id={`suggestions-list-${qaId}`}
                            style={toLowercase ? TEXT_TO_LOWERCASE : {}}
                        >
                            {suggestions.map((item, index) => {
                                return (
                                    <Item
                                        key={index}
                                        highlighted={highlightedIndex === index}
                                        data-qa-id={`suggestions-item-${qaId}`}
                                        data-value={this.getSearchValue(item)}
                                        onClick={() => {
                                            this.onItemSelect(item);
                                        }}
                                    >
                                        {this.getSearchValue(item)}
                                    </Item>
                                );
                            })}
                        </List>
                    )}
                </DropdownWrapper>
            </div>
        );
    }
}
